import React, { useState, useEffect, useRef, useMemo, MouseEvent } from 'react';
import { Plus, Minus, Maximize2, Minimize2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import utilityService from '../../../shared/utils/utilityService';
import { useTime } from '../../../shared/context/TimeContext';
import { useAuth } from '../../auth/context/AuthContext';
import {
  formatTo12Hour,
  parseTimeToSeconds,
  calculateRemainingSeconds,
  getElementTimingsFromElements,
  getRunningElementTimings,
  plannedBaselineMap,
  summarizeVariance,
  Element,
  LocalizedRunningState,
} from '@thekwallace/backtime-shared';
import {
  formatVarianceMagnitude,
  runPlannedBaselineSeconds,
  varianceGlyph,
  varianceTone,
  varianceWord,
} from '../utils/runningState';
import DisplayPanel, { CueModel, NowStripModel, RunStripModel } from './DisplayPanel';

interface CountdownTimerProps {
  selectedStartTime: string | null;
  occurrenceStartAbsolute?: number | null;
  elements?: Element[];
  /**
   * Live running state for this occurrence, when one is being operated —
   * ALREADY LOCALIZED into this browser's absolute-seconds frame by App.tsx.
   *
   * The wire form carries ISO instants and commits to no frame; this component
   * only ever does arithmetic, so it takes the form where the frame question is
   * already answered. It cannot re-localize even if it wanted to: the converter
   * needs a synced (nowMs, nowSeconds) pair, and having two consumers each grab
   * their own is how the two halves of the screen drift apart.
   *
   * The hero is the largest number on the page, so it must not be the one thing
   * still showing PLANNED time while the cards below it show actual. Null (the
   * normal case) projects to the planned timings verbatim, so nothing changes
   * for a timeline that is not being operated.
   */
  runningState?: LocalizedRunningState | null;
  timeOffset?: number;
  onTimeOffsetChange?: (offset: number) => void;
  isPublic?: boolean;
  publicCode?: string | null;
  readOnly?: boolean;
  noUpcomingOccurrences?: boolean;
  isOccurrenceComplete?: boolean;
  dayName?: string | null;
}

interface TimeDisplayState {
  timeRemaining: string;
  description: string;
  currentTime: string;
  remainingSeconds: number;
  /** Caption above the big number. Always 'Starts in' outside a live occurrence. */
  countdownLabel: string;
  /** True when the element in progress has run past where it should have handed off. */
  isOverdue: boolean;
  /** True when a run has ENDED, so there is nothing happening and nothing to count. */
  isFinished: boolean;
  /** What is in progress right now. Null outside a live run. */
  now: NowStripModel | null;
  /** How the run is tracking, and the next immovable deadline. Null outside a live run. */
  run: RunStripModel | null;
  /** Wall-clock time the cue lands. Null outside a live run. */
  cueAtClock: string | null;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({
  selectedStartTime,
  occurrenceStartAbsolute = null,
  elements = [],
  runningState = null,
  timeOffset = 0,
  onTimeOffsetChange,
  isPublic = false,
  publicCode = '',
  readOnly = false,
  noUpcomingOccurrences = false,
  isOccurrenceComplete = false,
  dayName = null
}) => {
  // UI state (toggle states only)
  const [showClock, setShowClock] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const fullscreenRef = useRef<HTMLDivElement>(null);

  // Get synchronized time from centralized TimeContext (updates every second)
  const { getTimeWithOffset } = useTime();
  const currentSeconds = getTimeWithOffset(timeOffset);

  // Check authentication for showing offset controls
  const { currentUser } = useAuth();
  const isAuthenticated = !!currentUser;

  // Sync state with browser fullscreen changes (e.g., ESC key exit)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Compute all time-related display values from centralized time
  const { timeRemaining, description, currentTime, remainingSeconds, countdownLabel, isOverdue, isFinished, now, run, cueAtClock }: TimeDisplayState = useMemo(() => {
    // Calculate current clock time display using shared utility
    const clockTime = formatTo12Hour(currentSeconds, true);

    // Normalized time for non-absolute calculations
    const normalizedCurrent = ((currentSeconds % 86400) + 86400) % 86400;

    const idle: Pick<
      TimeDisplayState,
      'countdownLabel' | 'isOverdue' | 'isFinished' | 'now' | 'run' | 'cueAtClock'
    > = {
      countdownLabel: 'Starts in',
      isOverdue: false,
      isFinished: false,
      now: null,
      run: null,
      cueAtClock: null,
    };

    // Handle completed occurrences (manual mode viewing historical occurrences)
    if (isOccurrenceComplete) {
      return { timeRemaining: '--:--', description: 'Occurrence complete', currentTime: clockTime, remainingSeconds: 0, ...idle };
    }

    // Handle no occurrences within window
    if (noUpcomingOccurrences) {
      return { timeRemaining: '--:--', description: 'Nothing upcoming', currentTime: clockTime, remainingSeconds: 0, ...idle };
    }

    // Handle empty or invalid selectedStartTime
    if (!selectedStartTime || selectedStartTime.trim() === '') {
      return { timeRemaining: '--:--', description: 'Set a start time', currentTime: clockTime, remainingSeconds: 0, ...idle };
    }

    // "A run exists" — the SAME predicate TimeElementList uses. It must stay the
    // same: an ABANDONED state is truthy but is not a run, and gating the pin below
    // on mere truthiness left the hero anchored to a discarded run's occurrence
    // while the list followed the live selection. Owners never saw it (the DELETE
    // response nulls their state); viewers, who only ever receive the abandon
    // BROADCAST and so store `status: 'abandoned'`, saw the hero and the list forty
    // minutes apart on the same element.
    const hasRun = runningState?.status === 'running' || runningState?.status === 'completed';

    // A finished run is not a countdown. Without this the hero fell back to the
    // planned scan below and counted down to an element the run had already ended
    // before reaching — "Message / Starts in / 21:29" directly under a banner
    // reading "Occurrence complete".
    if (runningState?.status === 'completed') {
      return {
        timeRemaining: '--:--',
        description: 'Occurrence complete',
        currentTime: clockTime,
        remainingSeconds: 0,
        ...idle,
        isFinished: true,
      };
    }

    // Use absolute seconds if provided, otherwise fall back to normalized
    const selectedOccurrenceSeconds = occurrenceStartAbsolute !== null
      ? occurrenceStartAbsolute
      : parseTimeToSeconds(selectedStartTime);

    // Pinned to the occurrence the run was STARTED against, not the one currently
    // selected — auto-advance moves the selection mid-run (plan §9.1).
    //
    // Comes off the localized state, so the planned baseline below and the
    // actuals in `runningState.observations` are guaranteed to be in one frame —
    // App localized both with the same converter in one pass. This replaces a
    // hand-rolled conversion of `occurrenceStart` that was correct but re-derived
    // the frame here, i.e. one more place to get it wrong (plan §5, "build
    // planned from localized.occurrenceStartSeconds").
    //
    // `runPlannedBaselineSeconds` removes `timeOffset` from it, because that
    // offset moves the observer's clock and not the schedule — the same reason
    // `selectedOccurrenceSeconds` below carries no offset either. Both branches
    // therefore mean the same thing, and agree exactly when offset is 0.
    const occurrenceStartSeconds = hasRun
      ? runPlannedBaselineSeconds(runningState, timeOffset)
      : selectedOccurrenceSeconds;

    // Use matching current time (absolute if occurrence is absolute, normalized otherwise)
    const currentForCalc = occurrenceStartAbsolute !== null ? currentSeconds : normalizedCurrent;

    // Calculate countdown with day-aware logic.
    // Pass the RAW element tree: `getElementTimingsFromElements` flattens groups itself,
    // in tree order. Pre-flattening here strips the group container while leaving
    // `parentId` on its children, and the shared function's own flatten then discards
    // them as orphans — silently dropping the whole group from the chain. Same input as
    // the API uses, so the hero and the server cannot disagree.
    //
    // The planned timings are then overlaid with what has ACTUALLY happened. With
    // no running state `getRunningElementTimings` returns them verbatim, so every
    // branch below is byte-for-byte the behaviour that shipped before Operate Mode.
    const planned = getElementTimingsFromElements(occurrenceStartSeconds, elements);
    // Baseline from the run's snapshot, chain from the current elements — see the note
    // in TimeElementList. The hero must measure against the same plan the list does, or
    // the two disagree again, which is the defect this feature has already shipped twice.
    const projected = getRunningElementTimings(
      planned,
      runningState,
      currentForCalc,
      plannedBaselineMap(runningState, occurrenceStartSeconds)
    );

    // ---- The run strip: pace, plus the next thing that cannot move ----
    // The operator is not really managing "variance"; they are managing the next
    // immovable deadline. Surfacing it here is what turns a number into a decision.
    const buildRunStrip = (fromIndex: number): RunStripModel => {
      const variance = summarizeVariance(projected).currentVariance;
      const tone = varianceTone(variance);
      const fixed = projected.find(
        (el, i) => i > fromIndex && el.anchorOffset !== null
      ) ?? null;
      return {
        magnitude: tone === 'on-time' ? '' : formatVarianceMagnitude(variance),
        word: varianceWord(variance),
        glyph: varianceGlyph(tone),
        tone,
        nextFixed: fixed
          ? { description: fixed.description, atClock: formatTo12Hour(fixed.plannedStart, true) }
          : null,
      };
    };

    // While an element is genuinely in progress the hero counts down to the HAND-OFF
    // — the moment the operator should cue the next thing — which is the same number
    // the in-progress card shows as "remaining". Before this, the hero read planned
    // time while the card two inches below read actual, and the two contradicted
    // each other outright (plan §9.1).
    const cursor = runningState?.status === 'running'
      ? projected.findIndex((el) => el.status === 'current')
      : -1;

    if (cursor >= 0) {
      const current = projected[cursor];
      const next = cursor + 1 < projected.length ? projected[cursor + 1] : null;
      // The in-progress element's projected END is stretched to `now` once it runs
      // long (that is what makes variance live), so it cannot be used to measure
      // remaining time. Measure from where it was NOMINALLY due to hand off.
      const handOff = (current.actualStart ?? current.startTime) + current.duration;
      const remaining = handOff - currentForCalc;

      // What IS happening, so the panel stops being a pure forecast. Measured from the
      // element's ACTUAL start — which is the whole point of having marked it.
      const elapsedSeconds = Math.max(0, currentForCalc - (current.actualStart ?? current.startTime));
      const nowStrip: NowStripModel = {
        description: current.description,
        elapsed: utilityService.formatCountdown(elapsedSeconds),
        budget: utilityService.formatCountdown(current.duration),
        progress: current.duration > 0 ? elapsedSeconds / current.duration : 0,
        over: elapsedSeconds > current.duration,
      };
      const runStrip = buildRunStrip(cursor);

      if (remaining > 0) {
        return {
          timeRemaining: utilityService.formatCountdown(remaining),
          description: next ? next.description : current.description,
          currentTime: clockTime,
          remainingSeconds: remaining,
          // `CUE IN` names what the operator is meant to DO, which is what makes the
          // panel read as a live instrument rather than a schedule.
          countdownLabel: next ? 'CUE IN' : 'ENDS IN',
          isOverdue: false,
          isFinished: false,
          now: nowStrip,
          run: runStrip,
          cueAtClock: formatTo12Hour(handOff, true),
        };
      }

      // Over-running. Showing "0:00" (or worse, "Happening Now") here would hide
      // the one number the operator most needs.
      return {
        timeRemaining: utilityService.formatCountdown(-remaining),
        description: next ? next.description : current.description,
        currentTime: clockTime,
        remainingSeconds: 0,
        countdownLabel: next ? 'OVERDUE BY' : 'OVER BY',
        isOverdue: true,
        isFinished: false,
        now: nowStrip,
        run: runStrip,
        cueAtClock: formatTo12Hour(handOff, true),
      };
    }

    // Started, but nothing marked yet — the operator is holding for the first cue.
    //
    // The CUE deliberately keeps falling through to the planned scan below: with nothing
    // marked there is no actual to project from, so planned time is still the best
    // available answer, and that fallback is a documented regression guard. What was
    // missing is that the panel gave no sign a run existed at all, which is precisely
    // when the operator most needs to see one. So this ADDS the strips and leaves the
    // countdown alone.
    // No element is in progress, which now means only one thing: the clock has not
    // reached the first element yet. There is no "started but nothing marked" state any
    // more — the plan is assumed to be running, so once the occurrence begins something
    // always is. The NOW strip has nothing to report, and says nothing.
    const waitingTail = runningState?.status === 'running'
      ? {
          countdownLabel: 'CUE IN',
          isOverdue: false,
          isFinished: false,
          now: null as NowStripModel | null,
          run: buildRunStrip(-1),
          cueAtClock: null as string | null,
        }
      : idle;

    const nextElement = projected.find((el) => el.startTime > currentForCalc) ?? null;

    if (nextElement) {
      // Show countdown to next upcoming element
      const remaining = calculateRemainingSeconds(currentForCalc, nextElement.startTime);
      return {
        timeRemaining: utilityService.formatCountdown(remaining),
        description: nextElement.description,
        currentTime: clockTime,
        remainingSeconds: remaining,
        ...waitingTail,
        cueAtClock: waitingTail.now ? formatTo12Hour(nextElement.startTime, true) : null,
      };
    } else if (currentForCalc < occurrenceStartSeconds) {
      // Before occurrence starts - show countdown to occurrence start
      const remaining = calculateRemainingSeconds(currentForCalc, occurrenceStartSeconds);
      return {
        timeRemaining: utilityService.formatCountdown(remaining),
        description: 'Occurrence starts',
        currentTime: clockTime,
        remainingSeconds: remaining,
        ...waitingTail,
      };
    } else {
      // All elements have started - occurrence is happening now
      return {
        timeRemaining: '0:00',
        description: 'Happening Now',
        currentTime: clockTime,
        remainingSeconds: 0,
        ...waitingTail,
      };
    }
    // `timeOffset` is read directly (not only through `currentSeconds`) now that
    // the planned baseline has to have it removed — see runPlannedBaselineSeconds.
  }, [currentSeconds, selectedStartTime, occurrenceStartAbsolute, elements, runningState, timeOffset, noUpcomingOccurrences, isOccurrenceComplete]);

  // Handle mouse movement to show/hide controls in fullscreen
  useEffect(() => {
    if (!isFullscreen) return;

    let timeout: NodeJS.Timeout;
    const handleMouseMove = () => {
      setShowControls(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setShowControls(false);
      }, 5000);
    };

    handleMouseMove(); // Show controls initially
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearTimeout(timeout);
    };
  }, [isFullscreen]);

  const handleAdjustOffset = (seconds: number, e: MouseEvent) => {
    e.stopPropagation();
    if (onTimeOffsetChange) {
      onTimeOffsetChange(timeOffset + seconds);
    }
  };

  const handleResetOffset = (e: MouseEvent) => {
    e.stopPropagation();
    if (onTimeOffsetChange) {
      onTimeOffsetChange(0);
    }
  };

  const handleFullscreenToggle = async (e: MouseEvent) => {
    e.stopPropagation();

    // Check both native fullscreen and CSS fallback state
    if (!document.fullscreenElement && !isFullscreen) {
      // Enter fullscreen
      try {
        if (fullscreenRef.current) {
          await fullscreenRef.current.requestFullscreen();
        }
      } catch (err) {
        console.error('Fullscreen error:', err);
        // Fallback to CSS fullscreen if API fails
        setIsFullscreen(true);
      }
    } else {
      // Exit fullscreen
      if (document.fullscreenElement) {
        try {
          await document.exitFullscreen();
        } catch (err) {
          console.error('Exit fullscreen error:', err);
          setIsFullscreen(false);
        }
      } else {
        // CSS fallback mode - just update state
        setIsFullscreen(false);
      }
    }
  };

  const getCountdownTextColor = (): string => {
    if (remainingSeconds <= 120) { // 2 minutes or less - red
      return 'text-red-500';
    } else if (remainingSeconds <= 300) { // 5 minutes or less - yellow
      return 'text-yellow-500';
    } else { // more than 5 minutes - green
      return 'text-green-500';
    }
  };

  const getBorderColor = (): string => {
    // Over-running reads as urgent, not as the calm blue of "happening now".
    if (isOverdue) {
      return 'border-red-700/50';
    }
    if (remainingSeconds === 0) {
      return 'border-blue-700/50';
    } else if (remainingSeconds <= 120) {
      return 'border-red-700/50';
    } else if (remainingSeconds <= 300) {
      return 'border-yellow-700/50';
    } else {
      return 'border-green-700/50';
    }
  };

  // Build the full URL for public timeline sharing
  const timelineUrl = isPublic && publicCode ? `${window.location.origin}/t/${publicCode}` : '';

  // ONE cue model for both branches. The inline and fullscreen markup were
  // near-duplicates; deriving the content here and sizing it with a prop is what stops
  // them drifting into disagreement again (plan §9.1, §9.2).
  const isLiveRun = runningState?.status === 'running';
  const cue: CueModel = {
    description,
    label: countdownLabel,
    value: timeRemaining,
    atClock: cueAtClock,
    showCountdown: remainingSeconds > 0 || isOverdue,
    isOverdue,
  };
  const showHappeningNow = !noUpcomingOccurrences && !isFinished;

  if (isFullscreen) {
    return (
      <div ref={fullscreenRef} className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center p-8 cursor-pointer animate-fadeIn"
        onClick={() => setShowClock(!showClock)}
      >
        {/* QR Code in top-left corner */}
        {isPublic && timelineUrl && !isLiveRun && (
          <div className="absolute top-4 left-4 bg-white p-3 rounded-lg shadow-2xl z-10">
            <QRCodeSVG
              value={timelineUrl}
              size={120}
              level="M"
              includeMargin={false}
            />
          </div>
        )}

        <button
          onClick={handleFullscreenToggle}
          className={`absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-all duration-500 hover:scale-110 z-10 ${
            showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          title="Exit fullscreen"
        >
          <Minimize2 className="w-6 h-6 text-white" />
        </button>

        <div className="text-center animate-scaleIn">
          {showClock ? (
            <>
              <div className="text-9xl font-bold text-white mb-8 font-mono tracking-tight drop-shadow-2xl transition-all duration-300">
                {currentTime}
              </div>
              <div className="text-4xl text-blue-200 font-medium transition-all duration-300">
                <div className="flex flex-col items-center gap-6">
                  {isAuthenticated && onTimeOffsetChange && (
                    <div className="flex items-center gap-4" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={(e) => handleAdjustOffset(-1, e)}
                        className="p-3 bg-red-600 hover:bg-red-700 rounded-lg transition-all duration-300 hover:scale-110"
                        title="Decrease by 1 second"
                      >
                        <Minus className="w-6 h-6" />
                      </button>
                      <div className="text-xl text-gray-300 min-w-[120px]">
                        Offset: {timeOffset > 0 ? '+' : ''}{timeOffset}s
                      </div>
                      <button
                        onClick={(e) => handleAdjustOffset(1, e)}
                        className="p-3 bg-green-600 hover:bg-green-700 rounded-lg transition-all duration-300 hover:scale-110"
                        title="Increase by 1 second"
                      >
                        <Plus className="w-6 h-6" />
                      </button>
                      {timeOffset !== 0 && (
                        <button
                          onClick={handleResetOffset}
                          className="ml-3 px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition-all duration-300 hover:scale-105 text-base"
                          title="Reset offset to 0"
                        >
                          Reset
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <DisplayPanel
              scale="fullscreen"
              cue={cue}
              now={now}
              run={run}
              valueColorClass={getCountdownTextColor()}
              showHappeningNow={showHappeningNow}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={fullscreenRef}
      className="relative group"
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      {/* QR Code in top-left corner */}
      {isPublic && timelineUrl && !isLiveRun && (
        <div className="absolute top-2 left-2 bg-white p-2 rounded-lg shadow-lg z-10">
          <QRCodeSVG
            value={timelineUrl}
            size={80}
            level="M"
            includeMargin={false}
          />
        </div>
      )}

      <button
        onClick={handleFullscreenToggle}
        className={`absolute top-2 right-2 p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all duration-500 hover:scale-110 z-10 ${
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        title="Enter fullscreen"
      >
        <Maximize2 className="w-4 h-4 text-white" />
      </button>

      <div
        className={`bg-black ${getBorderColor()} rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 shadow-2xl border backdrop-blur-sm cursor-pointer transition-all duration-500 hover:scale-[1.02] active:scale-[0.98]`}
        onClick={() => setShowClock(!showClock)}
      >
        <div className="text-center">
          {showClock ? (
            <>
              <div className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-2 sm:mb-3 md:mb-4 font-mono tracking-tight drop-shadow-lg">
                {currentTime}
              </div>
              <div className="text-base sm:text-lg md:text-xl lg:text-2xl text-blue-200 font-medium">
                <div className="flex flex-col items-center gap-3">
                  {isAuthenticated && onTimeOffsetChange && (
                    <div className="flex items-center gap-2 sm:gap-3" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={(e) => handleAdjustOffset(-1, e)}
                        className="p-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                        title="Decrease by 1 second"
                      >
                        <Minus className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                      <div className="text-xs sm:text-sm text-gray-300 min-w-[80px] sm:min-w-[100px]">
                        Offset: {timeOffset > 0 ? '+' : ''}{timeOffset}s
                      </div>
                      <button
                        onClick={(e) => handleAdjustOffset(1, e)}
                        className="p-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                        title="Increase by 1 second"
                      >
                        <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                      {timeOffset !== 0 && (
                        <button
                          onClick={handleResetOffset}
                          className="ml-2 px-2 py-1 sm:px-3 sm:py-1.5 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors text-xs sm:text-sm"
                          title="Reset offset to 0"
                        >
                          Reset
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <DisplayPanel
              scale="inline"
              cue={cue}
              now={now}
              run={run}
              valueColorClass={getCountdownTextColor()}
              showHappeningNow={showHappeningNow}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CountdownTimer;
